import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Pencil, Search, PackageOpen, Lock, Unlock, Truck, RefreshCw, Receipt, Shuffle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const num = (n) => (n || 0).toLocaleString("id-ID");

export default function PreOrderBatches() {
  const { can } = useAuth();
  const write = can("warehouse_admin", "sales");
  const isOwner = can("owner");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [edit, setEdit] = useState(null);
  const [arrival, setArrival] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [redistributing, setRedistributing] = useState(false);
  const [reclassing, setReclassing] = useState(false);
  const [unmatched, setUnmatched] = useState(null);
  const [salesSummary, setSalesSummary] = useState(null);

  const load = () => api.get("/preorder-batches", { params: { q: q || undefined, status: statusF } })
    .then((r) => setRows(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, statusF]);

  const toggleStatus = async (b) => {
    try { await api.post(`/preorder-batches/${b.id}/status`, { status: b.status === "Open" ? "Closed" : "Open" }); toast.success("Status batch diperbarui"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (b) => {
    if (!window.confirm(`Hapus batch ${b.name}?`)) return;
    try { await api.delete(`/preorder-batches/${b.id}`); toast.success("Batch dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const reconcile = async () => {
    if (!window.confirm("Rekonsiliasi kuota batch dari seluruh riwayat penerimaan produksi?\n\nMengisi kolom Terisi sampai Planned mengikuti Prioritas batch (khusus produk 'Selalu simpan sebagai Stok PO'). Tidak menyentuh stok fisik. Aman dijalankan berulang.")) return;
    setReconciling(true);
    try {
      const r = await api.post("/preorder-batches/reconcile-production");
      toast.success(r.data?.message || "Rekonsiliasi selesai");
      if ((r.data?.unmatched_list || []).length) setUnmatched(r.data.unmatched_list);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setReconciling(false); }
  };

  const redistribute = async () => {
    if (!window.confirm("Ratakan ulang 'Barang Datang' antar batch mengikuti Prioritas batch?\n\nUntuk tiap varian: total barang datang dituang ulang — Keep batch prioritas dipenuhi lebih dulu, sisanya jadi Stok PO siap jual. Varian yang SUDAH ada penjualan dilewati demi keamanan. Tidak menyentuh stok fisik. Aman diulang.")) return;
    setRedistributing(true);
    try {
      const r = await api.post("/preorder-batches/redistribute-arrivals");
      toast.success(r.data?.message || "Ratakan ulang selesai");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRedistributing(false); }
  };

  const reclassify = async () => {
    if (!window.confirm("Pindahkan stok produk PO yang masih di Ready (Home/Store) ke bucket Pre Order?\n\nHanya untuk produk ber-flag 'Selalu simpan sebagai Stok PO'. Idempoten & aman dijalankan berulang.")) return;
    setReclassing(true);
    try {
      const r = await api.post("/products/reclassify-po-stock");
      toast.success(r.data?.message || `${r.data?.moved_qty || 0} pcs dipindah ke Pre Order (${r.data?.moved_rows || 0} baris)`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setReclassing(false); }
  };

  return (
    <div data-testid="preorder-batches-page">
      <PageHeader title="Batch Open PO" subtitle="Campaign Pre-Order — kuota per varian, terpisah dari stok fisik">
        {isOwner && <Button onClick={reclassify} disabled={reclassing} variant="outline" className="border-cocoa/30 text-cocoa hover:bg-cocoa/5" data-testid="pob-reclass-btn"><PackageOpen className="w-4 h-4 mr-1.5" />{reclassing ? "Memindahkan..." : "Reklasifikasi PO"}</Button>}
        {write && <Button onClick={redistribute} disabled={redistributing} variant="outline" className="border-cocoa/30 text-cocoa hover:bg-cocoa/5" data-testid="pob-redistribute-btn"><Shuffle className={`w-4 h-4 mr-1.5 ${redistributing ? "animate-spin" : ""}`} />{redistributing ? "Meratakan..." : "Ratakan Ulang Barang Datang"}</Button>}
        {write && <Button onClick={reconcile} disabled={reconciling} variant="outline" className="border-cocoa/30 text-cocoa hover:bg-cocoa/5" data-testid="pob-reconcile-btn"><RefreshCw className={`w-4 h-4 mr-1.5 ${reconciling ? "animate-spin" : ""}`} />{reconciling ? "Merekonsiliasi..." : "Rekonsiliasi PO"}</Button>}
        {write && <Button onClick={() => setEdit({})} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="pob-add-btn"><Plus className="w-4 h-4 mr-1.5" /> Buat Batch</Button>}
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / kode batch..." className="pl-9 bg-white" data-testid="pob-search" />
        </div>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="w-40 bg-white" data-testid="pob-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "Open", "Closed", "Arrived", "Done"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "Semua Status" : s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Panel>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Kode</TableHead><TableHead>Nama Batch</TableHead><TableHead>Estimasi Datang</TableHead>
              <TableHead className="text-center">Prioritas</TableHead>
              <TableHead className="text-center">Varian</TableHead>
              <TableHead className="text-right">Planned</TableHead><TableHead className="text-right">Terisi</TableHead><TableHead className="text-right">Reserved</TableHead>
              <TableHead className="text-right">Sold</TableHead><TableHead className="text-right">Sisa</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((b) => (
              <TableRow key={b.id} data-testid={`pob-row-${b.id}`}>
                <TableCell className="font-mono text-sm text-cocoa">{b.code}</TableCell>
                <TableCell className="font-medium text-cocoa">{b.name}
                  <div className="text-[10px] text-wmuted font-normal" data-testid={`pob-groups-${b.id}`}>{b.group_names?.length ? b.group_names.join(", ") : "Semua group"}</div>
                </TableCell>
                <TableCell className="text-sm">{b.est_arrival_date || "-"}</TableCell>
                <TableCell className="text-center"><span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full bg-cocoa/10 text-cocoa text-xs font-semibold" data-testid={`pob-priority-${b.id}`}>{b.priority ?? 100}</span></TableCell>
                <TableCell className="text-center">{b.totals?.line_count || 0}</TableCell>
                <TableCell className="text-right">{num(b.totals?.planned)}</TableCell>
                <TableCell className="text-right" data-testid={`pob-filled-${b.id}`}><span className="font-semibold text-cocoa">{num(b.totals?.arrived)}</span><span className="text-[10px] text-wmuted">/{num(b.totals?.planned)}</span></TableCell>
                <TableCell className="text-right text-amber-700">{num(b.totals?.reserved)}</TableCell>
                <TableCell className="text-right text-blue-700">{num(b.totals?.sold)}</TableCell>
                <TableCell className="text-right font-bold text-status-passed" data-testid={`pob-remaining-${b.id}`}>{num(b.totals?.remaining)}</TableCell>
                <TableCell><Badge variant="outline" className={b.status === "Open" ? "text-status-passed border-green-300" : "text-wmuted"}>{b.status}</Badge></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {write && <Button size="icon" variant="ghost" onClick={() => toggleStatus(b)} className="h-8 w-8 text-cocoa" title={b.status === "Open" ? "Tutup" : "Buka"} data-testid={`pob-toggle-${b.id}`}>{b.status === "Open" ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}</Button>}
                  {write && <Button size="icon" variant="ghost" onClick={() => setArrival(b)} className="h-8 w-8 text-blue-600" title="Input Kedatangan" data-testid={`pob-arrival-${b.id}`}><Truck className="w-4 h-4" /></Button>}
                  <Button size="icon" variant="ghost" onClick={() => setSalesSummary(b)} className="h-8 w-8 text-blue-700" title="Riwayat Jual PO" data-testid={`pob-sales-${b.id}`}><Receipt className="w-4 h-4" /></Button>
                  {write && <Button size="icon" variant="ghost" onClick={() => setEdit(b)} className="h-8 w-8 text-cocoa" data-testid={`pob-edit-${b.id}`}><Pencil className="w-4 h-4" /></Button>}
                  {write && <Button size="icon" variant="ghost" onClick={() => remove(b)} className="h-8 w-8 text-status-reject" data-testid={`pob-del-${b.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={12} className="text-center text-wmuted py-10"><PackageOpen className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada batch Pre-Order.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Panel>

      {edit && <BatchDialog batch={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {arrival && <ArrivalDialog batch={arrival} onClose={() => setArrival(null)} onDone={() => { setArrival(null); load(); }} />}
      {unmatched && <UnmatchedDialog rows={unmatched} onClose={() => setUnmatched(null)} />}
      {salesSummary && <SalesSummaryDialog batch={salesSummary} onClose={() => setSalesSummary(null)} />}
    </div>
  );
}

function ArrivalDialog({ batch, onClose, onDone }) {
  const [lines, setLines] = useState([]);
  const [source, setSource] = useState("Produksi");
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/preorder-batches/${batch.id}`).then((r) => {
      setLines((r.data.lines || []).map((l) => ({ po_quota_id: l.id, label: `${l.product_name} — ${l.color}/${l.size}`, reserved: l.reserved_po || 0, arrived: l.arrived_qty || 0, qty: "" })));
    }).catch(() => {});
  }, [batch.id]);

  const setQty = (i, v) => { const n = [...lines]; n[i] = { ...n[i], qty: v }; setLines(n); };

  const submit = async () => {
    const payload = { source, ref, lines: lines.filter((l) => parseInt(l.qty) > 0).map((l) => ({ po_quota_id: l.po_quota_id, qty: parseInt(l.qty) })) };
    if (!payload.lines.length) return toast.error("Isi minimal satu qty kedatangan");
    setBusy(true);
    try {
      const r = await api.post(`/preorder-batches/${batch.id}/arrivals`, payload);
      const totAlloc = (r.data.result || []).reduce((a, x) => a + x.allocated_to_keep, 0);
      const totLeft = (r.data.result || []).reduce((a, x) => a + x.leftover, 0);
      toast.success(`Kedatangan dicatat · ${totAlloc} siap kirim (FIFO), ${totLeft} jadi stok PO siap jual`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="pob-arrival-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Kedatangan PO — {batch.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Sumber</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger className="bg-white" data-testid="arr-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Produksi">Produksi/Konveksi</SelectItem>
                  <SelectItem value="PO">Purchase Order</SelectItem>
                  <SelectItem value="Manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Ref (opsional)</Label><Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="No. produksi/PO" className="bg-white" data-testid="arr-ref" /></div>
          </div>
          <div className="border border-taupe/20 rounded-lg max-h-72 overflow-y-auto" data-testid="arr-lines">
            <div className="grid grid-cols-[1fr_70px_80px] gap-2 px-3 py-2 bg-cocoa/5 text-xs font-bold text-cocoa uppercase">
              <div>Varian</div><div className="text-center">Reserved</div><div className="text-center">Datang</div>
            </div>
            {lines.map((l, i) => (
              <div key={l.po_quota_id} className="grid grid-cols-[1fr_70px_80px] gap-2 items-center px-3 py-2 border-t border-taupe/10 text-sm">
                <span className="text-cocoa truncate">{l.label}</span>
                <span className="text-center text-amber-700">{l.reserved}</span>
                <Input type="number" min={0} value={l.qty} onChange={(e) => setQty(i, e.target.value)} className="h-8 text-center" data-testid={`arr-qty-${i}`} />
              </div>
            ))}
            {!lines.length && <div className="text-center text-wmuted py-6 text-sm">Batch tanpa varian.</div>}
          </div>
          <p className="text-xs text-wmuted">Barang datang otomatis dialokasikan FIFO ke keep Pre-Order (jadi "Siap Kirim"); kelebihannya menjadi Stok PO siap jual.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="arr-submit"><Truck className="w-4 h-4 mr-1.5" />{busy ? "Memproses..." : "Catat Kedatangan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BatchDialog({ batch, onClose, onSaved }) {
  const isEdit = !!batch.id;
  const [form, setForm] = useState({ name: batch.name || "", est_arrival_date: batch.est_arrival_date || "", open_date: batch.open_date || "", close_date: batch.close_date || "", status: batch.status || "Open", priority: batch.priority ?? 100, note: batch.note || "", group_ids: batch.group_ids || [] });
  const [groups, setGroups] = useState([]);
  const [lines, setLines] = useState([]);
  const [flat, setFlat] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const toggleGroup = (id) => setForm((f) => ({ ...f, group_ids: f.group_ids.includes(id) ? f.group_ids.filter((x) => x !== id) : [...f.group_ids, id] }));

  useEffect(() => {
    api.get("/customer-groups", { params: { active: "active" } }).then((r) => setGroups(r.data)).catch(() => {});
    api.get("/products/inventory").then((r) => {
      const f = [];
      (r.data.products || []).forEach((p) => (p.variants || []).forEach((v) => f.push({
        variant_id: v.variant_id, label: `${p.name} — ${v.color}/${v.size}`,
      })));
      setFlat(f);
    }).catch(() => {});
    if (isEdit) api.get(`/preorder-batches/${batch.id}`).then((r) => {
      setLines((r.data.lines || []).map((l) => ({ variant_id: l.variant_id, label: `${l.product_name} — ${l.color}/${l.size}`, planned_qty: l.planned_qty, arrived: l.arrived_qty || 0, reserved: l.reserved_po || 0, locked: (l.reserved_po || 0) + (l.sold_po || 0) > 0 })));
    }).catch(() => {});
  }, [batch.id, isEdit]);

  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    const used = new Set(lines.map((l) => l.variant_id));
    return flat.filter((f) => !used.has(f.variant_id) && f.label.toLowerCase().includes(t)).slice(0, 20);
  }, [q, flat, lines]);

  const addLine = (f) => { setLines([...lines, { variant_id: f.variant_id, label: f.label, planned_qty: 0, locked: false }]); setQ(""); };
  const setQty = (i, v) => { const n = [...lines]; n[i] = { ...n[i], planned_qty: Math.max(0, parseInt(v) || 0) }; setLines(n); };
  const delLine = (i) => setLines(lines.filter((_, j) => j !== i));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama batch wajib diisi");
    if (!lines.length) return toast.error("Tambahkan minimal satu varian");
    const payload = { ...form, lines: lines.map((l) => ({ variant_id: l.variant_id, planned_qty: parseInt(l.planned_qty) || 0 })) };
    setSaving(true);
    try {
      if (isEdit) await api.put(`/preorder-batches/${batch.id}`, payload);
      else await api.post("/preorder-batches", payload);
      toast.success("Batch disimpan"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="pob-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">{isEdit ? "Edit Batch Open PO" : "Buat Batch Open PO"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>Nama Batch *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="mis. PO Lebaran 2026" className="bg-white" data-testid="pob-name" /></div>
            <div><Label>Estimasi Datang</Label><Input type="date" value={form.est_arrival_date || ""} onChange={(e) => setForm({ ...form, est_arrival_date: e.target.value })} className="bg-white" data-testid="pob-arrival" /></div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-white" data-testid="pob-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Open">Open</SelectItem><SelectItem value="Closed">Closed</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Prioritas Isi (auto FIFO)</Label><Input type="number" min={1} value={form.priority} onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })} className="bg-white" data-testid="pob-priority" /><span className="text-[10px] text-wmuted">Angka kecil = diisi lebih dulu saat kedatangan produksi & "Ratakan Ulang Barang Datang"</span></div>
          </div>

          <div>
            <Label>Customer Group (boleh lebih dari satu; kosong = semua group)</Label>
            <div className="mt-1 flex flex-wrap gap-2 border border-taupe/20 rounded-lg p-2 max-h-32 overflow-y-auto" data-testid="pob-group-select">
              {groups.map((g) => {
                const on = form.group_ids.includes(g.id);
                return <button type="button" key={g.id} onClick={() => toggleGroup(g.id)} data-testid={`pob-group-${g.id}`} className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? "bg-cocoa text-ivory border-cocoa" : "bg-white text-cocoa border-taupe/30 hover:border-cocoa/50"}`}>{g.name}</button>;
              })}
              {!groups.length && <span className="text-xs text-wmuted">Belum ada customer group.</span>}
            </div>
          </div>

          <div>
            <Label>Tambah Varian</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk untuk ditambahkan..." className="pl-9 bg-white" data-testid="pob-variant-search" />
              {matches.length > 0 && (
                <div className="absolute z-30 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-taupe/30 rounded-md shadow-lg" data-testid="pob-variant-options">
                  {matches.map((f) => <button key={f.variant_id} onClick={() => addLine(f)} className="block w-full text-left px-3 py-2 text-sm hover:bg-cocoa/5" data-testid={`pob-variant-opt-${f.variant_id}`}>{f.label}</button>)}
                </div>
              )}
            </div>
          </div>

          <div className="border border-taupe/20 rounded-lg max-h-64 overflow-y-auto" data-testid="pob-lines">
            {lines.map((l, i) => (
              <div key={l.variant_id} className="flex items-center gap-2 px-3 py-2 border-b border-taupe/10 last:border-0 text-sm">
                <span className="flex-1 text-cocoa">{l.label}</span>
                {isEdit && <span className="text-[10px] px-1.5 py-0.5 rounded bg-cocoa/10 text-cocoa whitespace-nowrap" data-testid={`pob-line-arrived-${i}`}>Datang {l.arrived || 0}</span>}
                <div className="flex items-center gap-1">
                  <span className="text-xs text-wmuted">Slot</span>
                  <Input type="number" min={0} value={l.planned_qty} onChange={(e) => setQty(i, e.target.value)} className="w-24 h-8 text-right" data-testid={`pob-line-qty-${i}`} />
                </div>
                {l.locked ? <Lock className="w-4 h-4 text-amber-600" title="Sudah ada keep/penjualan" /> : <button onClick={() => delLine(i)} data-testid={`pob-line-del-${i}`}><Trash2 className="w-4 h-4 text-status-reject" /></button>}
              </div>
            ))}
            {!lines.length && <div className="text-center text-wmuted py-6 text-sm">Belum ada varian.</div>}
          </div>

          <div><Label>Catatan</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} className="bg-white" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} disabled={saving} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="pob-save-btn">{saving ? "Menyimpan..." : "Simpan Batch"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function UnmatchedDialog({ rows, onClose }) {
  const total = rows.reduce((a, r) => a + (r.qty || 0), 0);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="pob-unmatched-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Penerimaan tanpa padanan batch ({num(total)} pcs)</DialogTitle></DialogHeader>
        <p className="text-sm text-wmuted">Varian berikut sudah diterima dari produksi tetapi belum punya baris kuota di batch Open manapun (dicocokkan via produk + warna + ukuran). Tambahkan varian ini ke batch, lalu jalankan Rekonsiliasi PO lagi.</p>
        <div className="border border-taupe/20 rounded-lg overflow-hidden mt-2">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Produk</TableHead><TableHead>Varian</TableHead><TableHead className="text-right">Qty</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i} data-testid={`pob-unmatched-row-${i}`}>
                  <TableCell className="text-cocoa">{r.product_name || "-"}</TableCell>
                  <TableCell>{r.color}/{r.size}</TableCell>
                  <TableCell className="text-right font-semibold">{num(r.qty)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <DialogFooter><Button onClick={onClose} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="pob-unmatched-close">Tutup</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SalesSummaryDialog({ batch, onClose }) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/preorder-batches/${batch.id}`).then((r) => setLines(r.data.lines || [])).catch(() => {}).finally(() => setLoading(false));
  }, [batch.id]);
  const sold = lines.filter((l) => (l.sold_po || 0) > 0);
  const totalSold = lines.reduce((a, l) => a + (l.sold_po || 0), 0);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pob-sales-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Riwayat Jual PO — {batch.name}</DialogTitle></DialogHeader>
        <div className="text-sm text-wmuted">Total terjual: <span className="font-bold text-blue-700" data-testid="pob-sales-total">{num(totalSold)}</span> pcs dari batch ini.</div>
        <div className="border border-taupe/20 rounded-lg overflow-hidden mt-2">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Produk</TableHead><TableHead>Varian</TableHead><TableHead className="text-right">Planned</TableHead><TableHead className="text-right">Datang</TableHead><TableHead className="text-right">Reserved</TableHead><TableHead className="text-right">Terjual</TableHead><TableHead className="text-right">Sisa</TableHead></TableRow></TableHeader>
            <TableBody>
              {sold.map((l) => (
                <TableRow key={l.id} data-testid={`pob-sold-row-${l.id}`}>
                  <TableCell className="text-cocoa">{l.product_name}</TableCell>
                  <TableCell>{l.color}/{l.size}</TableCell>
                  <TableCell className="text-right">{num(l.planned_qty)}</TableCell>
                  <TableCell className="text-right">{num(l.arrived_qty)}</TableCell>
                  <TableCell className="text-right text-amber-700">{num(l.reserved_po)}</TableCell>
                  <TableCell className="text-right font-bold text-blue-700">{num(l.sold_po)}</TableCell>
                  <TableCell className="text-right font-semibold text-status-passed">{num(l.remaining_po)}</TableCell>
                </TableRow>
              ))}
              {!loading && !sold.length && <TableRow><TableCell colSpan={7} className="text-center text-wmuted py-8">Belum ada penjualan PO pada batch ini.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        <DialogFooter><Button onClick={onClose} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="pob-sales-close">Tutup</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

